import 'swiper/css'
import 'swiper/css/navigation'

import { MapDirectionsRenderer } from '@c/Catalog'
import { FilteredRoutesList } from '@c/Catalog/Map/components/FilteredRoutesList'
import { StartExcursionSection } from '@c/Catalog/Map/components/StartExcursionSection'
import { circleOptions, createCenterControl, mapOptions } from '@c/Catalog/Map/mapStyler'
import { createMarkerIcon } from '@c/Catalog/Map/utils/createMarkerIcon'
import { GoogleMap, Marker, TransitLayer } from '@react-google-maps/api'
import debounce from 'lodash/debounce'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactGA from 'react-ga4'
import { useTranslation } from 'react-i18next'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import { IconClose } from '@/components/Ui/Icons'
import { IconBack } from '@/components/Ui/Icons/IconBack'
import { Coordinates, IMapObject, InterestPoint, Language, type MapPoint } from '@/core/interface'
import { Excursion, type TExcursionPoint } from '@/core/interface/Excursion'
import { ExcursionCategory } from '@/core/interface/ExcursionCategory'
import { useAppDispatch, useAppSelector } from '@/core/store'
import { getPointsThunk } from '@/core/store/catalog.store'
import {
  setLocale,
  setLocationManual,
  setMapAutoCentering,
  setMapLoaded,
  setUserLocation,
} from '@/core/store/ui.store'

type Props = {
  data: any
  active: boolean
  onClose: () => void
  startExcursion: (id: string, firstPointId: string) => void
  closeExcursion: any
  markers: InterestPoint[]
  isExcursionBeingSelected: boolean
  setExcursionBeingSelected: (state: boolean) => void
  initialExcursionId?: string
}

const RoutesWindow = (props: Props) => {
  const { active, onClose, startExcursion, markers, isExcursionBeingSelected, initialExcursionId } =
    props
  const { userLocation, userPosition, locale, locationManual, audioPlaying, mapAutoCentering } =
    useAppSelector((store) => store.uiState)
  const { excursions, excursionCategories, isPayedClient, payedTours } = useAppSelector(
    (store) => store.catalogStore,
  )
  const { modal } = useAppSelector((store) => store.uiState)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { i18n, t } = useTranslation('routes')
  const location = useLocation()

  const [initialExcursion, setInitialExcursion] = useState<string | null>(
    initialExcursionId !== undefined ? initialExcursionId : null,
  )
  const [activeCategory, setActiveCategory] = useState<'all' | string>('all')
  const [viewRoute, setViewRoute] = useState<Excursion | null>(null)
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [markDto, setPointerDataset] = useState<IMapObject | null>(null)
  const [currentZoom, setCurrentZoom] = useState<number>(13)
  const [places, setPlaces] = useState<MapPoint[]>([])
  const [descriptionModal, setDescriptionModal] = useState(false)
  const [descriptionText, setDescriptionText] = useState('Loading...')

  const [trackModal, setTrackModal] = useState(false)
  const [nextStep, setNextStep] = useState(false)
  const [lastViewRoute, setLastViewRoute] = useState<Excursion | null>(null)

  //Swiping position to manage arrows and bold elements
  const [currentSlideIndices, setCurrentSlideIndices] = useState<{ [key: string]: number }>({})

  //Pasrse url parameters
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    // if (modal == null && trackModal && isPayedClient) {
    if (modal == null && trackModal) {
      setTrackModal(false)
    }
    setTrackModal(modal == 'payment')
  }, [modal])

  //If we go back to landing and select excursions from there again,
  // we need to reset the state values
  useEffect(() => {
    if (initialExcursionId !== undefined) {
      setInitialExcursion(initialExcursionId)
    }
  }, [initialExcursionId])

  const initialExcursionRef = useRef<{ [key: string]: HTMLDivElement | null }>({})

  const handleCategoryChange = (category: string) => {
    setActiveCategory(category)
    ReactGA.event({
      category: 'excursion',
      action: 'WA_CategoryChange',
      label: category,
    })

    // Reset indices for the new set of excursions
    const newExcursions = excursions.filter((exc) => exc.category === category)
    const newIndices = newExcursions.reduce((acc, exc) => {
      acc[exc.id] = 0
      return acc
    }, {} as { [key: string]: number })
    setCurrentSlideIndices(newIndices)
  }

  useEffect(() => {
    if (viewRoute?.landmarks !== undefined) {
      return setNextStep(true)
    }
    return setNextStep(false)
  }, [viewRoute])

  //When initializing with specific category and route to show first
  // OR if tour selected move to selected tour
  useEffect(() => {
    let initialTour = ''
    if (searchParams.get('tour')) initialTour = searchParams.get('tour') || ''
    if (excursions.length > 0 && (initialTour != '' || initialExcursion)) {
      let excursion = excursions[0]
      if (initialTour == '')
        excursion = excursions.find((exc) => exc.id === initialExcursion) || excursions[0]
      else excursion = excursions.find((exc) => exc.id === initialTour) || excursions[0]

      if (excursion) {
        if (isPayedClient || payedTours.includes(excursion.id)) {
          setLastViewRoute(excursion)
          setViewRoute(excursion)
          const adjustedPoints = adjustCoordinates(excursion.landmarks)
          setPlaces(adjustedPoints)
          ReactGA.event({
            category: 'excursion',
            action: 'WA_ExcursionClick',
            label: excursion.id,
          })
        }
        if (excursion.category != activeCategory) handleCategoryChange(excursion.category)
        setTimeout(() => {
          const element = initialExcursionRef.current[initialExcursion || initialTour]
          if (element) {
            // element.scrollIntoView({ behavior: 'instant', block: 'start' })
            // Instant scroll to selected excursion
            element.scrollIntoView(true)
          }
        }, 100)
      }
      // Clear the initialExcursion after scrolling
      setInitialExcursion(null)
    }
  }, [excursions, initialExcursion, viewRoute, payedTours])

  useEffect(() => {
    if (lastViewRoute && viewRoute === null) {
      handleCategoryChange(lastViewRoute.category)
      setTimeout(() => {
        const element = initialExcursionRef.current[lastViewRoute.id]

        if (element) {
          // element.scrollIntoView({ behavior: 'instant', block: 'start' })
          // Instant scroll to selected excursion
          element.scrollIntoView(true)
        }
        setLastViewRoute(null)
      }, 100)
    }
    // Clear the initialExcursion after scrolling
  }, [viewRoute])

  // Variant that selects the selected excursion
  // useEffect(() => {
  //   if (excursions.length > 0 && initialExcursion) {
  //     const excursion = excursions.find((exc) => exc.id === initialExcursion)
  //     if (excursion) {
  //       setViewRoute(excursion)
  //       const adjustedPoints = adjustCoordinates(excursion.points)
  //       setPlaces([...adjustedPoints.map((point: { coordinates: any }) => point.coordinates)])

  //       // Scroll to the excursion after a short delay
  //       setTimeout(() => {
  //         initialExcursionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  //       }, 100)
  //     }
  //     // Clear the initial excursion to prevent re-running this effect
  //     setInitialExcursion(null)
  //   }
  // }, [excursions, initialExcursion])

  // position setter around the positioning
  const [customLatLng, setCustomLatLng] = useState<{
    latitude: number
    longitude: number
  } | null>(null)

  useEffect(() => {
    const screenWidth = window.screen.width
    const zoom = screenWidth < 800 ? 17 : screenWidth < 1200 ? 18 : 19
    if (location.state) {
      const pos = { latitude: location.state.lat, longitude: location.state.lon }
      location.state = undefined
      setLocationManual(true)
      setCustomLatLng(pos)
      setCurrentZoom(zoom)
    }
  })

  //When several points are located at exactly the same spot,
  //we artificially move subsequent ones a little on the map,
  //so they would not be fully overlapped and become visible
  const adjustCoordinates = (points: TExcursionPoint[]) => {
    const seen = new Map()
    return points.map((point, index) => {
      const key = `${point.coordinate.latitude},${point.coordinate.longitude}`
      if (seen.has(key)) {
        const i = 1 + seen.get(key)
        const offset = i * 0.0005 // ~25 meters
        seen.set(key, i)
        return {
          ...point,
          coordinate: {
            latitude: point.coordinate.latitude,
            longitude: point.coordinate.longitude + offset,
          },
        }
      } else {
        seen.set(key, 0)
        return point
      }
    })
  }

  // Callback function to create marker icons
  // Used for both route points and user location
  type CreateMarkerIconParams = {
    window: any
    index: number
    category: string
    zoom?: number
    heading?: number | null
  }

  // Provide default values for mapCenter and userLocation
  const defaultCenter = { lat: 41.89021, lng: 12.4923 } // Center of Rome
  const defaultUserLocation = { latitude: 41.89021, longitude: 12.4923 }

  const mapCenter = useMemo(() => {
    if (customLatLng) {
      return {
        lat: customLatLng.latitude,
        lng: customLatLng.longitude,
      }
    }

    if (mapAutoCentering && userLocation) {
      return {
        lat: userLocation.latitude,
        lng: userLocation.longitude,
      }
    }

    return defaultCenter
  }, [userLocation, customLatLng, mapAutoCentering])

  // map events
  const onLoad = useCallback(function callback(map: google.maps.Map) {
    dispatch(setMapLoaded(false))
    setMap(map)

    const bounds = new window.google.maps.LatLngBounds()
    markers.forEach((marker) => {
      bounds.extend(
        new google.maps.LatLng(marker.coordinate?.latitude, marker.coordinate?.longitude),
      )
    })

    //Reset button
    const centerControlDiv = document.createElement('div')
    centerControlDiv.classList.add('centerControl')
    centerControlDiv.classList.add('hide')
    const centerControl = createCenterControl({ onClickHandler: resetLocation })
    centerControlDiv.appendChild(centerControl)

    map.controls[google.maps.ControlPosition.LEFT_TOP].push(centerControlDiv)

    setMap(map)
    dispatch(setMapLoaded(true))
  }, [])

  const onUnmount = useCallback(function callback(_map: google.maps.Map | null) {
    setMap(null)
  }, [])

  useEffect(() => {
    if (mapAutoCentering) {
      const centerBtn = document.querySelector('.centerControl')
      // @ts-ignore
      centerBtn?.classList.add('hide')
    } else {
      const centerBtn = document.querySelector('.centerControl')
      // @ts-ignore
      centerBtn?.classList.remove('hide')
    }
  }, [mapAutoCentering])

  const handleMarkerClick = useCallback(async (e: any, marker: InterestPoint) => {
    setPointerDataset({
      xid: marker.id,
      name: marker.title,
      point: {
        lat: marker.coordinate?.latitude,
        lon: marker.coordinate?.longitude,
      },
      info: {
        descr: marker.description,
      },
      preview: {
        source: marker.gallery?.[0]?.uri,
      },
      gallery: marker?.gallery ?? [],
      audioFile: marker.audioURI || undefined,
    })
  }, [])

  const onZoomChanged = useCallback(() => {
    const zoom = map?.getZoom()

    setCurrentZoom(zoom ?? 13)
  }, [map])

  const onDragEnd = useCallback(() => {
    dispatch(setMapAutoCentering(false))
  }, [map])

  const fetchPosition = useCallback(
    debounce(async (newLocation: Coordinates) => {
      dispatch(getPointsThunk())
    }, 300),
    [i18n.language],
  )

  const shareUrl = `${window.location.origin}/map?tour=`

  const resetLocation = useCallback(() => {
    setCustomLatLng(null)
    dispatch(setMapAutoCentering(true))
    try {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          let { latitude, longitude } = position.coords
          //TODO: Make it a nicer solution
          const isNotRomePosition =
            (latitude > 42.05 || latitude < 41.72) && (longitude < 12.146 || longitude > 12.7777)
          if (isNotRomePosition) {
            latitude = 41.89021
            longitude = 12.4923
          }
          dispatch(setUserLocation({ latitude, longitude }))
          dispatch(getPointsThunk())
          dispatch(setLocationManual(false))
          map?.setCenter({ lat: latitude, lng: longitude })
        },
        (error) => {
          console.error('Error obtaining location:', error)
          //TODO: another hack
          dispatch(setUserLocation({ latitude: 41.89021, longitude: 12.4923 }))
        },
        {
          enableHighAccuracy: true,
          timeout: 1000,
          maximumAge: Infinity,
        },
      )
    } catch {
      console.error('Geo error')
    }
  }, [locale])

  const renderCount = useRef<number>(0)
  useEffect(() => {
    dispatch(setLocale(i18n.language as Language))
    if (renderCount.current >= 1) {
      resetLocation()
    }

    renderCount.current = renderCount.current + 1
  }, [i18n.language])

  useEffect(() => {
    if (customLatLng) {
      fetchPosition({
        latitude: customLatLng.latitude,
        longitude: customLatLng.longitude,
      })
    }
  }, [customLatLng])

  useEffect(() => {
    if (locationManual && map && userLocation) {
      map.setCenter({ lat: userLocation.latitude, lng: userLocation.latitude })
    }
  }, [locationManual])

  const getMemoizedMarkerList = useCallback(
    (excursion: Excursion, mode: 'preview' | 'full_access') => {
      if (!window.google) return [] //TODO: this could result in racing condition (map not loaded yet, but markers are - fix this)
      if (excursion.landmarks.length) {
        const adjustedPoints = adjustCoordinates(excursion.landmarks)
        return adjustedPoints
          .filter((item) => item.title)
          .map((marker, idx: number) => {
            return {
              key: idx,
              icon: createMarkerIcon({
                window: window,
                idx: idx,
                rank: marker.rank,
                iconUsing: 'route',
                isHighlighted: false,
                selectedPoint: '',
                id: null,
                category: marker.type,
                zoom: map?.getZoom(),
                heading: undefined,
              }),
              clickable: true,
              onClick: (e: google.maps.MapMouseEvent) => {
                if (mode === 'preview') return
                setViewRoute(excursion)
                setNextStep(true)
              },
              position: {
                lat: marker.coordinate.latitude,
                lng: marker.coordinate.longitude,
              },
              isOpen: false,
            }
          })
      }
      return markers.map((marker, idx) => ({
        key: idx,
        icon: createMarkerIcon({
          window: window,
          idx: idx,
          iconUsing: 'route',
          id: null,
          rank: marker.rank,
          isHighlighted: false,
          selectedPoint: '',
          category: marker.type,
          zoom: map?.getZoom(),
          heading: undefined,
        }),
        clickable: true,
        onClick: (e: google.maps.MapMouseEvent) => {
          if (mode === 'preview') return
          handleMarkerClick(e, marker)
        },
        position: {
          lat: marker.coordinate?.latitude,
          lng: marker.coordinate?.longitude,
        },
        isOpen: false,
      }))
    },
    [markers, currentZoom, viewRoute],
  )

  return (
    <>
      {generatePaymentBlock()}
      {generateDescriptionBlock(descriptionText)}

      <div className={'routes__window' + (active ? ' active' : '')}>
        {viewRoute && generateRoutesWindowBackButton()}
        {generateCloseRoutesWindowButton()}
        {!viewRoute && <p className="routes__title">{t('firstScreen.title')}</p>}
        {!viewRoute ? (
          <>
            {generateRoutesCategoriesHeader()}
            <FilteredRoutesList
              initialExcursionRef={initialExcursionRef}
              map={map}
              currentZoom={currentZoom}
              onLoad={onLoad}
              onUnmount={onUnmount}
              isPayedClient={isPayedClient}
              activeCategory={activeCategory}
              adjustCoordinates={adjustCoordinates}
              currentSlideIndices={currentSlideIndices}
              excursions={excursions}
              getMemoizedMarkerList={getMemoizedMarkerList}
              mapCenter={mapCenter}
              onDragEnd={onDragEnd}
              onZoomChanged={onZoomChanged}
              payedTours={payedTours}
              setCurrentSlideIndices={setCurrentSlideIndices}
              setLastViewRoute={setLastViewRoute}
              setViewRoute={setViewRoute}
              setPlaces={setPlaces}
              userPosition={userPosition}
            />
          </>
        ) : (
          <>
            <StartExcursionSection
              viewRoute={viewRoute}
              nextStep={nextStep}
              onClose={onClose}
              setViewRoute={setViewRoute}
              startExcursion={startExcursion}
            />
            <div className="view__wrap">
              <GoogleMap
                mapContainerStyle={{
                  width: '100%',
                  height: 'calc(50vh - 100px)',
                  position: 'relative',
                  marginTop: '10px',
                }}
                center={mapCenter}
                zoom={currentZoom}
                onLoad={onLoad}
                onUnmount={onUnmount}
                onZoomChanged={onZoomChanged}
                onDragEnd={onDragEnd}
                options={mapOptions as any}
              >
                {places.length > 0 && (
                  <MapDirectionsRenderer places={places} travelMode={'WALKING'} />
                )}
                <TransitLayer />
                {getMemoizedMarkerList(viewRoute, 'full_access').map(
                  ({ key, icon, clickable, onClick, position }, index) => (
                    <Marker
                      icon={icon}
                      key={key}
                      clickable={clickable}
                      onClick={onClick}
                      position={position}
                      zIndex={1000 - index}
                    />
                  ),
                )}
                <Marker
                  key={80000000}
                  options={circleOptions}
                  icon={createMarkerIcon({
                    window: window,
                    idx: null,
                    iconUsing: 'route',
                    id: null,
                    rank: 1,
                    isHighlighted: false,
                    selectedPoint: '',
                    category: 'user-current-location',
                    zoom: map?.getZoom(),
                    heading: userPosition.heading,
                  })}
                  position={{ lat: userPosition.latitude, lng: userPosition.longitude }}
                />
              </GoogleMap>
              {!nextStep && (
                <div className="routes__item-dotsNames">
                  <div className="routes__item-column">
                    {viewRoute.landmarks
                      .filter((item) => item.title)
                      .slice(0, Math.ceil(viewRoute.landmarks.length / 2))
                      .map((point: any, index: number) => (
                        <p className="routes__item-dot-title" key={point.id}>
                          {index !== 0 && <span>•</span>}
                          {index + 1}. {point.title}
                        </p>
                      ))}
                  </div>
                  <div className="routes__item-column">
                    {viewRoute.landmarks
                      .filter((item) => item.title)
                      .slice(Math.ceil(viewRoute.landmarks.length / 2))
                      .map((point: any, index: number) => (
                        <p className="routes__item-dot-title" key={point.id}>
                          {index + Math.ceil(viewRoute.landmarks.length / 2) !== 0 && (
                            <span>•</span>
                          )}
                          {index + Math.ceil(viewRoute.landmarks.length / 2) + 1}. {point.title}
                        </p>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  )

  function generatePaymentBlock() {
    return (
      <>
        {/* <div className={`buy_modal ${buyModal ? 'active' : ''}`}>
          {generateRouteCloseButton()}
          <p className={`buy_title`}>Only 3 stories can be played for free</p>
          <p className="buy_text">Purchase full access for €2.99?</p>
          <div className="buttons">
            <button
              onClick={() => {
                setBuyModal(false)
                setThanksModal(true)
              }}
              className={'close'}
            >
              NO WAY!
            </button>
            <button
              onClick={() => {
                setBuyModal(false)
                setThanksModal(true)
              }}
              className={'apply'}
            >
              PURCHASE
            </button>
          </div>
        </div> */}
        {/* <div className={`thanks_modal ${thanksModal ? 'active' : ''}`}>
          <button
            className="routes__close"
            onClick={() => {
              setThanksModal(false)
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="34"
              height="24"
              viewBox="0 0 34 24"
              fill="none"
            >
              <path
                d="M26.8331 5L7.38867 19"
                stroke="#2B3537"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7.38867 5L26.8331 19"
                stroke="#2B3537"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <p className={`buy_title`}>Congratulations! </p>
          <p className="buy_text">
            You have won free access to our service. Enjoy and give us your feedback! */}
        {/* As one of the first users of our application you get free access for the duration of our
            testing. Please continue using the app and give us some feedback! */}
        {/* </p>

          <button
            onClick={() => {
              setThanksModal(false)
            }}
            className={'close'}
          >
            OK!
          </button>
        </div> */}
      </>
    )
  }

  function generateDescriptionBlock(description = 'no description :(') {
    return (
      <>
        <div className={`description_modal ${descriptionModal ? 'active' : ''}`}>
          <button
            className="routes__close"
            onClick={() => {
              setDescriptionModal(false)
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="34"
              height="24"
              viewBox="0 0 34 24"
              fill="none"
            >
              <path
                d="M26.8331 5L7.38867 19"
                stroke="#2B3537"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7.38867 5L26.8331 19"
                stroke="#2B3537"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <p className={`description_title`}>Tour description</p>
          <p className="description_text" dangerouslySetInnerHTML={{ __html: description }}></p>

          <button
            onClick={() => {
              setDescriptionModal(false)
            }}
            className={'close'}
          >
            OK!
          </button>
        </div>{' '}
      </>
    )
  }

  function generateRouteCloseButton() {
    return (
      <button
        className="routes__close"
        onClick={() => {
          // setBuyModal(false)
          // setThanksModal(true)
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="34"
          height="24"
          viewBox="0 0 34 24"
          fill="none"
        >
          <path
            d="M26.8331 5L7.38867 19"
            stroke="#2B3537"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M7.38867 5L26.8331 19"
            stroke="#2B3537"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    )
  }

  function generateRoutesCategoriesHeader() {
    function changeCategory(category: ExcursionCategory) {
      handleCategoryChange(category.id)
      ReactGA.event({
        category: 'excursion',
        action: 'WA_CategoryClick',
        label: category.name,
      })
    }

    return (
      <div className={`routes__categories`}>
        {excursionCategories.map((category) => (
          <button
            onClick={() => changeCategory(category)}
            className={'routes__category' + (activeCategory === category.id ? ' active' : '')}
            key={category.id}
          >
            <p className="routes__category-title">{category.name}</p>
          </button>
        ))}
      </div>
    )
  }

  function generateCloseRoutesWindowButton() {
    return (
      <button
        className="routes__close"
        onClick={() => {
          setViewRoute(null)
          onClose()
          setNextStep(false)
          ReactGA.event({
            category: 'excursion',
            action: 'WA_CloseRoutes',
          })

          navigate('/map', { replace: true })
        }}
      >
        <IconClose></IconClose>
      </button>
    )
  }

  function generateRoutesWindowBackButton() {
    return (
      <button
        className="routes__back"
        onClick={() => {
          setViewRoute(null)
          setNextStep(false)
        }}
      >
        <IconBack></IconBack>
      </button>
    )
  }
}

export default RoutesWindow
